import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { List, Task } from "../../types";
import { DEFAULT_LIST_EMOJI } from "../../constants/defaults";

export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("lists").delete().eq("id", id);
      if (error) throw error;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.lists });
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });

      const prevLists = queryClient.getQueryData<List[]>(queryKeys.lists);
      const prevTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Each cache is left alone when it has not loaded: writing `[]` into it
      // would flip its query out of its pending state and drop the app's
      // loading gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<List[]>(queryKeys.lists, (old) =>
        old ? old.filter((l) => l.id !== id) : old
      );

      // FK is ON DELETE SET NULL — tasks in the deleted list become listless
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (old) =>
        old
          ? old.map((t) =>
              t.list_id === id
                ? { ...t, list_id: null, list: "", listEmoji: DEFAULT_LIST_EMOJI }
                : t
            )
          : old
      );

      return { prevLists, prevTasks };
    },

    onError: (err, _, context) => {
      if (context?.prevLists)
        queryClient.setQueryData<List[]>(queryKeys.lists, context.prevLists);
      if (context?.prevTasks)
        queryClient.setQueryData<Task[]>(queryKeys.tasks, context.prevTasks);
      Sentry.captureException(err, { extra: { mutation: "useDeleteList" } });
      toast.error("Failed to delete list. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lists });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },
  });
}
